import React, { useEffect, useState, useCallback, useRef } from "react";
import { Spin, Typography, Tooltip, message, Checkbox } from "antd";
import { useParams, useLocation } from "react-router-dom";
import MyVideoPlayer from "./MyVideoPlayer";
import { DownOutlined, RightOutlined, CheckCircleFilled } from '@ant-design/icons';
import ApiService from '../../services/ApiService';
import { useLearningProgress, useAuth } from '../../contexts/AuthContext';
import KeycloakService from '../../services/KeycloakService';
import { Role } from '../../constants/Role';
import '../../styles/LearnCourse.css';
import DOMPurify from 'dompurify';
import CommentSection from '../../components/ui/CommentSection';
import MentorCourseApiService from '../../services/MentorCourseApiService';

const { Title } = Typography;

const MAX_COURSE_NAME = 60
const MAX_SECTION_NAME = 40; 
const MAX_LESSON_NAME = 28;

const TICK_MESSAGES = [
  "🎉 Congratulations! You've completed this course.",
  "✅ Course marked as complete. Well done!",
  "🏆 Success! Course completed.",
  "🌟 You've finished this course. Great job!",
  "📚 All lessons completed. Course marked as done.",
  "🙌 Hooray! You've successfully finished the course.",
  "🎓 Course completion confirmed. Congrats!",
  "👍 Well done! This course is now complete.",
  "✔️ You just completed this course. Keep learning!",
  "🥳 Fantastic! You've marked the course as finished.",
  "🚀 Way to go! Course marked as completed.",
  "💾 Completion saved. You finished the course!",
  "🏅 Excellent! This course is now in your completed list.",
  "👏 You did it! Course completed and saved.",
  "🎯 Course finished. Keep up the good work!",
  "🌈 Awesome! You've marked this course as complete.",
  "🎊 Your course progress is complete. Congrats!",
  "🥇 Well done! Course status updated to completed.",
  "📝 Course successfully set as complete.",
  "⭐ This course is now marked as finished. Great job!"
];
const UNTICK_MESSAGES = [
  "⚠️ Course marked as incomplete.",
  "🔄 You've unmarked this course as complete.",
  "🚫 Course completion status removed.",
  "⬜ Course set to incomplete.",
  "↩️ Course completion undone.",
  "⏳ Course unmarked as finished.",
  "🔙 You've set this course back to incomplete.",
  "🗑️ Course progress reset to incomplete.",
  "🛑 This course is no longer marked as complete.",
  "⏪ Completion status reverted. Course is now incomplete.",
  "🗂️ You removed this course from your completed list.",
  "🔄 The course is now marked as in progress.",
  "🌀 You've reset the completion status for this course.",
  "📝 Completion undone. You can continue learning!",
  "🔁 Course status changed to incomplete.",
  "❌ Course completion mark removed.",
  "🔧 You've updated the course as incomplete.",
  "📖 The course is now back in your learning list.",
  "🕘 Incomplete status applied to this course.",
  "🚦 This course is now marked as incomplete."
];

function getRandomMsg(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Function to determine checkbox state and CSS class
function getCheckboxState(lesson, selectedLesson, completedLessons, lessonTracking, isMentor, isCourseCompleted) {
  if (isMentor) {
    return { className: 'lesson-checkbox-disabled', disabled: true };
  }
  if (isCourseCompleted) {
    return { className: 'lesson-checkbox-disabled', disabled: true };
  }
  const isCompleted = completedLessons.includes(lesson.id);
  const isCurrentLesson = selectedLesson?.lessonId === lesson.id;
  const isVideoLesson = lesson.type === "Video";
  const isTrackedCompleted = lessonTracking[lesson.id]?.completed;
  
  // Check if lesson has tracking info from API (for video lessons)
  const hasApiTracking = lesson.tracking && typeof lesson.tracking === 'object';
  const isApiTrackedCompleted = hasApiTracking && lesson.tracking.completed === true;
  
  if (isCompleted) {
    // Màu xanh: đã tick (khóa học đã hoàn thành và đã tick)
    return {
      className: 'lesson-checkbox-completed',
      disabled: false
    };
  }
  
  if ((isApiTrackedCompleted || isTrackedCompleted) && !isCompleted) {
    // Màu trắng viền xanh: được phép tick/untick (khóa học đã hoàn thành và muốn học lại)
    return {
      className: 'lesson-checkbox-retakeable', 
      disabled: false
    };
  }
  
  if (isCurrentLesson && isVideoLesson && !isCompleted && !isTrackedCompleted) {
    // Màu xám: khi click vào bài chưa được tick (khóa học đang học)
    return {
      className: 'lesson-checkbox-disabled',
      disabled: true
    };
  }
  
  // Màu trắng: hiển thị mặc định ở tất cả khóa học chưa học
  return {
    className: 'lesson-checkbox-default',
    disabled: false
  };
}

export default function LearnCourse() {
  const {courseSlug } = useParams();
  const location = useLocation();
  const [sections, setSections] = useState([]); 
  const [selectedLesson, setSelectedLesson] = useState(null); 
  const [lessonDetail, setLessonDetail] = useState(null); 
  const [secureVideoUrl, setSecureVideoUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [courseTitle, setCourseTitle] = useState("");
  const [completedLessons, setCompletedLessons] = useState([]); 
  const [activeTab, setActiveTab] = useState("overview");
  const [sectionCollapse, setSectionCollapse] = useState({}); 
  const { setProgress, progress } = useLearningProgress();
  const [updatingLessonId, setUpdatingLessonId] = useState(null);
  const [lessonTracking, setLessonTracking] = useState({});
  const sectionRefs = useRef({});
  const sidebarListRef = useRef();
  const sidebarHeaderRef = useRef();

  // Get auth context for role checking
  const { hasRole } = useAuth();
  
  // Detect mentor role from actual user role or query param
  const isMentor = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return hasRole(Role.STAFF_COURSE_MENTOR) || params.get('role') === 'mentor';
  }, [location.search, hasRole]);

  // Handle lesson tracking updates from video player
  const handleTrackingUpdate = useCallback((trackingData) => {
    if (selectedLesson?.lessonId) {
      setLessonTracking(prev => ({
        ...prev,
        [selectedLesson.lessonId]: trackingData
      }));
    }
  }, [selectedLesson]);

  // Handle completion change from video player
  const handleCompletionChange = useCallback((isCompleted) => {
    if (isCompleted && selectedLesson?.lessonId && !completedLessons.includes(selectedLesson.lessonId)) {
      // Auto-complete the lesson when video tracking indicates completion
      handleToggleComplete(selectedLesson.lessonId);
    }
  }, [selectedLesson, completedLessons]);
  const courseTitleRef = useRef();
  const [showCourseTooltip, setShowCourseTooltip] = useState(false);
  const [showFloatingNav, setShowFloatingNav] = useState(false);

  const fetchLessonDetail = useCallback(async (lessonId) => {
    setLessonDetail(null);
    setSecureVideoUrl("");
    try {
      let lesson, videoUrl = "";
      if (isMentor) {
        // Mentor: gọi API nội bộ lấy lesson detail
        const res = await MentorCourseApiService.getMentorLessonDetailById(lessonId);
        lesson = res.data?.lesson;
        if (!lesson) throw new Error("No lesson data");
        if (lesson.type === "Video") {
          try {
            const videoRes = await MentorCourseApiService.getMentorLessonVideoUrl(lessonId);
            videoUrl = videoRes.data?.signedUrl || "";
          } catch { videoUrl = ""; }
        }
      } else {
        // Learner: gọi API learner như cũ
        const res = await ApiService.getAxiosInstance().get(`/learner/lessons/${lessonId}`);
        lesson = res.data?.data?.lesson;
        if (!lesson) throw new Error("No lesson data");
        if (lesson.type === "Video") {
          try {
            const videoRes = await ApiService.getAxiosInstance().get(`/learner/lessons/${lessonId}/video-stream-url`);
            videoUrl = videoRes.data?.data?.signedUrl || "";
          } catch { videoUrl = ""; }
        }
      }
      setLessonDetail({
        id: lesson._id,
        title: lesson.name,
        overview: lesson.description,
        type: lesson.type,
        htmlContent: lesson.htmlContent,
        isCompleted: lesson.isCompleted,
        videoUrl: lesson.videoUrl,
      });
      setSecureVideoUrl(videoUrl);
    } catch (e) {
      setLessonDetail(null);
      setSecureVideoUrl("");
      message.error(e?.message || "Failed to load lesson detail");
    }
  }, [isMentor]);

  const handleSelectLesson = useCallback((sectionId, lessonId) => {
    setSectionCollapse(prev => ({ ...prev, [sectionId]: true })); // Đảm bảo section luôn mở khi chọn lesson
    setSelectedLesson({ sectionId, lessonId });
    setActiveTab("overview");
    fetchLessonDetail(lessonId);
    localStorage.setItem('learnCourseLastLesson', JSON.stringify({ courseSlug, sectionId, lessonId }));
  }, [fetchLessonDetail, courseSlug]);

  // Fetch sections & course title & progress từ API mới
  const fetchCourseLearningZone = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      if (isMentor) {
        // Mentor: gọi API nội bộ lấy chi tiết course theo slug
        const res = await MentorCourseApiService.getMentorCourseDetailBySlug(courseSlug);
        data = res.data;
      } else {
        // Learner: gọi API learner như cũ
        const res = await ApiService.getAxiosInstance().get(`/learner/courses/${courseSlug}`);
        data = res.data?.data;
      }
      if (!data) throw new Error("No course data");
      setCourseTitle(data.course?.name || "");
      // Map sections/lessons về đúng format UI
      const mappedSections = (data.sections || []).map(section => ({
        id: section._id,
        title: section.name,
        displayOrder: section.displayOrder,
        lessons: (section.lessons || []).map(lesson => ({
          id: lesson._id,
          title: lesson.name,
          overview: lesson.description,
          type: lesson.type,
          htmlContent: lesson.htmlContent,
          completed: !!lesson.isCompleted,
          tracking: lesson.tracking || null, // Thêm tracking info nếu có
        })),
      }));
      setSections(mappedSections);
      // Collapse all sections by default
      setSectionCollapse(mappedSections.reduce((acc, s) => ({ ...acc, [s.id]: false }), {}));
      // KHÔNG gọi handleSelectLesson ở đây nữa
      // Lấy danh sách lesson đã học từ API
      const completed = [];
      mappedSections.forEach(section => {
        section.lessons.forEach(lesson => {
          if (lesson.completed) completed.push(lesson.id);
        });
      });
      setCompletedLessons(completed);
      // Update progress context with THIS course's progress
      setProgress({
        totalLessons: data.progress?.totalLessons || 0,
        completedLessons: data.progress?.completedLessons || 0,
      });
    } catch (e) {
      message.error(e?.message || "Failed to load course data");
    }
    setLoading(false);
  }, [courseSlug, setProgress, isMentor]);

  // Section collapse/expand
  const handleToggleSection = (sectionId) => {
    setSectionCollapse((prev) => {
      const newCollapse = { ...prev, [sectionId]: !prev[sectionId] };
      if (!prev[sectionId]) {
        setTimeout(() => {
          const sectionEl = sectionRefs.current[sectionId];
          const sidebarEl = sidebarListRef.current;
          const headerEl = sidebarHeaderRef.current;
          if (sectionEl && sidebarEl) {
            const sectionTop = sectionEl.offsetTop;
            const headerOffset = (headerEl?.offsetHeight || 41) + 38;
            sidebarEl.scrollTo({
              top: sectionTop - headerOffset,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
      return newCollapse;
    });
  };

  // Đánh dấu đã học/huỷ học bài học
  const handleToggleComplete = async (lessonId) => {
    if (updatingLessonId) return; // Đang update lesson khác, bỏ qua thao tác mới
    
    const isCurrentlyCompleted = completedLessons.includes(lessonId);
    const isCurrentLesson = selectedLesson?.lessonId === lessonId;
    
    // Check if lesson is retakeable (tracking completed but not manually completed)
    const isTrackedCompleted = lessonTracking[lessonId]?.completed;
    const lesson = sections.flatMap(s => s.lessons).find(l => l.id === lessonId);
    const hasApiTracking = lesson?.tracking && typeof lesson.tracking === 'object';
    const isApiTrackedCompleted = hasApiTracking && lesson.tracking.completed === true;
    const isRetakeable = (isApiTrackedCompleted || isTrackedCompleted) && !completedLessons.includes(lessonId);
    
    // If lesson is not completed, not the current lesson, and not retakeable, show toast notification and prevent toggle
    if (!isCurrentlyCompleted && !isCurrentLesson && !isRetakeable) {
      message.warning("You can only manually toggle completion for the current lesson, already completed lessons, or lessons you've finished watching");
      return;
    }
    
    setUpdatingLessonId(lessonId);
    const isCompleted = !completedLessons.includes(lessonId);
    const prevCompletedLessons = [...completedLessons];
    const prevSections = JSON.parse(JSON.stringify(sections));
    setCompletedLessons((prev) => {
      if (isCompleted) {
        return [...prev, lessonId];
      } else {
        return prev.filter((id) => id !== lessonId);
      }
    });
    setSections((prevSectionsState) => prevSectionsState.map(section => ({
      ...section,
      lessons: section.lessons.map(lesson => lesson.id === lessonId ? { ...lesson, completed: isCompleted } : lesson)
    })));
    try {
      const res = await ApiService.getAxiosInstance().put(
        `/learner/lessons/${lessonId}/progress`,
        { isCompleted: isCompleted }
      );
       // After successfully updating, update progress with the response
       const progress = res.data?.data;
       if (progress) {
         setProgress({
           totalLessons: progress.totalLessons,
           completedLessons: progress.completedLessons,
         });
       }
       if (isCompleted) {
         message.success(getRandomMsg(TICK_MESSAGES));
       } else {
         message.open({
           content: getRandomMsg(UNTICK_MESSAGES),
           icon: <CheckCircleFilled style={{ color: '#faad14' }} />,
         });
       }
    } catch (e) {
      setCompletedLessons(prevCompletedLessons);
      setSections(prevSections);
      message.error(e?.message || "Failed to update lesson progress");
    } finally {
      setUpdatingLessonId(null);
    }
  };

  // Helper: cắt text và tooltip nếu dài (section: 2 dòng)
  const ellipsis = (text, max, lines = 1) => {
    if (!text) return '';
    if (lines === 2) {
      const words = text.split(' ');
      let result = '';
      let lineCount = 1;
      for (let i = 0; i < words.length; i++) {
        if ((result + ' ' + words[i]).length > max && lineCount === 1) {
          result += '\n' + words[i];
          lineCount++;
        } else {
          result += (result ? ' ' : '') + words[i];
        }
        if (lineCount === 2 && result.length > max * 2) break;
      }
      const isLong = text.length > result.length;
      return isLong ? (
        <Tooltip title={text}><span style={{ display: 'inline-block', whiteSpace: 'pre-line', overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: 44 }}>{result}…</span></Tooltip>
      ) : (
        <span style={{ display: 'inline-block', whiteSpace: 'pre-line', maxHeight: 44 }}>{result}</span>
      );
    }
    return text.length > max ? (
      <Tooltip title={text}><span>{text.slice(0, max)}…</span></Tooltip>
    ) : (
      text
    );
  };

  // Helper: số bài đã học trong section
  const getSectionCompleted = (section) => {
    const total = section.lessons?.length || 0;
    const done = section.lessons?.filter(l => completedLessons.includes(l.id)).length || 0;
    return { done, total };
  };

  // Fetch course learning zone on mount
  useEffect(() => {
    fetchCourseLearningZone();
    // Clear progress on unmount
    return () => setProgress({ totalLessons: 0, completedLessons: 0 });
    // eslint-disable-next-line
  }, [fetchCourseLearningZone]);

  // Sau khi setCourseTitle, kiểm tra có bị cắt dòng không
  useEffect(() => {
    if (courseTitleRef.current) {
      setShowCourseTooltip(courseTitleRef.current.scrollWidth > courseTitleRef.current.clientWidth);
    }
  }, [courseTitle]);

  // Sau khi fetch xong sections:
  useEffect(() => {
    if (sections.length > 0) {
      const last = localStorage.getItem('learnCourseLastLesson');
      if (last) {
        const { courseSlug: savedSlug, sectionId, lessonId } = JSON.parse(last);
        if (savedSlug === courseSlug) {
          // Kiểm tra lesson có tồn tại không
          const section = sections.find(s => s.id === sectionId);
          const lesson = section?.lessons.find(l => l.id === lessonId);
          if (section && lesson) {
            setSectionCollapse(prev => ({ ...prev, [sectionId]: true }));
            setSelectedLesson({ sectionId, lessonId });
            fetchLessonDetail(lessonId);
            return;
          }
        }
      }
      // Nếu không có trạng thái cũ, mặc định mở đầu tiên
      if (sections[0]?.lessons[0]) {
        setSectionCollapse(prev => ({ ...prev, [sections[0].id]: true }));
        setSelectedLesson({ sectionId: sections[0].id, lessonId: sections[0].lessons[0].id });
        fetchLessonDetail(sections[0].lessons[0].id);
      }
    }
  }, [sections, courseSlug, fetchLessonDetail]);

  // Flatten all lessons for global navigation
  const flatLessons = sections.flatMap(section =>
    section.lessons.map(lesson => ({
      sectionId: section.id,
      lessonId: lesson.id,
      title: lesson.title,
      type: lesson.type,
    }))
  );
  const currentIndex = flatLessons.findIndex(
    l => l.sectionId === selectedLesson?.sectionId && l.lessonId === selectedLesson?.lessonId
  );
  const prevLesson = currentIndex > 0 ? flatLessons[currentIndex - 1] : null;
  const nextLesson = currentIndex < flatLessons.length - 1 ? flatLessons[currentIndex + 1] : null;



  const handlePrevLesson = () => {
    if (prevLesson) handleSelectLesson(prevLesson.sectionId, prevLesson.lessonId);
  };
  const handleNextLesson = () => {
    if (nextLesson) handleSelectLesson(nextLesson.sectionId, nextLesson.lessonId);
  };

  console.log('Header height:', sidebarHeaderRef.current?.offsetHeight);

  // Tính trạng thái hoàn thành toàn bộ khóa học
  const totalLessons = progress?.totalLessons || 0;
  const isCourseCompleted = totalLessons > 0 && completedLessons.length === totalLessons;

  return (
    <div>
      <div className="learn-layout" style={{ position: 'relative' }}>
        <div className="learn-left" style={{ position: 'relative' }}>
          {/* course name */}
          <div style={{ maxWidth: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 12 }}>
            {showCourseTooltip ? (
              <Tooltip title={courseTitle}>
                <Title level={4} style={{ marginBottom: 12, fontWeight: 500, display: 'inline', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} ref={courseTitleRef}>
                  {courseTitle}
                </Title>
              </Tooltip>
            ) : (
              <Title level={4} style={{ marginBottom: 12, fontWeight: 500, display: 'inline', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} ref={courseTitleRef}>
                {courseTitle}
              </Title>
            )}
          </div>
          {/* Course Content */}
          {!lessonDetail ? (
            <div style={{ width: '100%', height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Spin size="large" />
            </div>
          ) : lessonDetail.type === "Video" ? (
            <>
              <MyVideoPlayer 
                style={{ width: '100%', borderRadius: 10, marginBottom: 16, background: '#111', height: 600 }} 
                url={secureVideoUrl}
                lessonId={selectedLesson?.lessonId}
                authToken={KeycloakService.getToken()}
                onTrackingUpdate={handleTrackingUpdate}
                initialTracking={lessonTracking[selectedLesson?.lessonId] || {}}
                onCompletionChange={handleCompletionChange}
                disableTracking={isMentor}
              />
            
            </>
          ) : lessonDetail.type === "HTML" ? (
            <div style={{ position: 'relative', width: '100%', borderRadius: 10, marginBottom: 0, background: '#f7f7f7', fontSize: 15, fontWeight: 400, overflowY: "auto", padding: 20, minHeight: 300, height: 600, wordBreak: "break-word" }} className="lesson-html-content">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    lessonDetail?.htmlContent || lessonDetail?.overview || "<i>No content</i>",
                    {
                      ALLOWED_TAGS: [
                        'b', 'i', 'u', 'em', 'strong', 'a', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                        'ul', 'ol', 'li', 'br', 'span', 'div', 'pre', 'code', 'blockquote',
                        'table', 'thead', 'tbody', 'tr', 'td', 'th', 'img','href','link','embed'
                      ],
                      ALLOWED_ATTR: [
                        'title', 'alt', 'src', 'class', 'style','iframe'
                      ]
                    }
                  )
                }}
              />
            </div>
          ) : (
            <div style={{ width: '100%', height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              No content
            </div>
          )}
          {/* Divider dưới cùng khung content */}
          <div style={{
            width: '100%',
            height: '2px',
            background: '#ececec',
            margin: '10px 0 0 0',
            
            border: 'none',
          }} />
          {/* Lesson Header */}
          {lessonDetail && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fff',
                  borderRadius: 12,
                  boxShadow: '0 2px 8px #e0e0e0',
                  // padding: '12px 16px',
                  margin: '32px 0 0 0',
                  position: 'relative',
                  minHeight: 56,
                }}
              >
                <Tooltip title={prevLesson ? prevLesson.title : ''} placement="left">
                  <button
                    onClick={handlePrevLesson}
                    disabled={!prevLesson}
                    style={{
                      background: '#1677ff',
                      border: 'none',
                      borderRadius: 12,
                      width: 30,
                      height: 55,
                      fontSize: 28,
                      color: '#fff',
                      boxShadow: '0 2px 8px #2221',
                      opacity: prevLesson ? 1 : 0.5,
                      cursor: prevLesson ? 'pointer' : 'not-allowed',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'background 0.2s, box-shadow 0.2s',
                      marginRight: 16,
                      padding: 0,
                    }}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M15 6L9 12L15 18" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </Tooltip>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: 22,
                    color: '#222',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    margin: '0 8px',
                  }}
                >
                  {lessonDetail.title}
                </span>
                <Tooltip title={nextLesson ? nextLesson.title : ''} placement="right">
                  <button
                    onClick={handleNextLesson}
                    disabled={!nextLesson}
                    style={{
                      background: '#1677ff',
                      border: 'none',
                      borderRadius: 12,
                      width: 30,
                      height: 55,
                      fontSize: 28,
                      color: '#fff',
                      boxShadow: '0 2px 8px #2221',
                      opacity: nextLesson ? 1 : 0.5,
                      cursor: nextLesson ? 'pointer' : 'not-allowed',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'background 0.2s, box-shadow 0.2s',
                      marginLeft: 16,
                      padding: 0,
                    }}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M9 6L15 12L9 18" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </Tooltip>
              </div>
            </>
          )}
          {/* Tabs: Overview & Comment */}
          <div className="learn-tabs">
            <button className={`learn-tab${activeTab==='overview'?' active':''}`} onClick={()=>setActiveTab('overview')}>Overview</button>
            <button className={`learn-tab${activeTab==='comment'?' active':''}`} onClick={()=>setActiveTab('comment')}>Comment</button>
          </div>
          {/* Tab content */}
          <div className={`learn-tab-content ${activeTab} ${activeTab === 'comment' ? 'active' : ''}`}>
            {activeTab === 'overview' ? (
              <div style={{
                whiteSpace: "pre-line",
                wordWrap: "break-word",
                wordBreak: "break-word",
                width: "100%"
              }}>{lessonDetail?.overview || "No overview."}</div>
            ) : (
              <CommentSection lessonId={selectedLesson?.lessonId} />
            )}
          </div>
        </div>
        <div className="learn-right">
          <div className="learn-course-content-header" ref={sidebarHeaderRef}>
            <Title level={4} style={{ fontWeight: 700, marginBottom: 0, color: "#2d3748" }}>
              Course Content
            </Title>
          </div>
          <div className="learn-course-content-list" ref={sidebarListRef}>
            {loading ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Spin size="large" />
              </div>
            ) : (
              <div>
                {sections.map((section) => {
                  const { done, total } = getSectionCompleted(section);
                  const isOpen = sectionCollapse[section.id];
                  return (
                    <div
                      key={section.id}
                      ref={el => sectionRefs.current[section.id] = el}
                      className={`learn-section${isOpen ? ' open' : ' closed'}`}
                      onClick={() => handleToggleSection(section.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div
                        className="learn-section-header"
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="learn-section-header-main">
                          <div className="section-title-2lines">{ellipsis(section.title, MAX_SECTION_NAME, 2)}</div>
                          <div className="learn-section-progress">
                            {done}/{total} lessons completed
                          </div>
                        </div>
                        <span className="learn-section-dropdown">
                          {isOpen ? <DownOutlined /> : <RightOutlined />}
                        </span>
                      </div>
                      {isOpen && (
                        <div className="learn-section-lessons">
                          {section.lessons?.map((lesson) => (
                            <div
                              key={lesson.id}
                              className={`learn-lesson${selectedLesson && selectedLesson.lessonId === lesson.id ? ' active' : ''}`}
                              onClick={e => { e.stopPropagation(); handleSelectLesson(section.id, lesson.id); }}
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <div
                                style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 32 }}
                                onClick={e => e.stopPropagation()}
                              >
                                {updatingLessonId === lesson.id ? (
                                  <Spin size="small" />
                                ) : (
                                  <Tooltip 
                                  title={
                                    isCourseCompleted
                                      ? "You have completed all lessons"
                                      : (selectedLesson?.lessonId === lesson.id && lesson.type === "Video" && !completedLessons.includes(lesson.id) && !lessonTracking[lesson.id]?.completed
                                          ? "Complete watching the video to enable manual completion toggle"
                                          : "")
                                  }
                                >
                                  <div className={getCheckboxState(lesson, selectedLesson, completedLessons, lessonTracking, isMentor, isCourseCompleted).className}>
                                    <Checkbox
                                      checked={completedLessons.includes(lesson.id)}
                                      disabled={getCheckboxState(lesson, selectedLesson, completedLessons, lessonTracking, isMentor, isCourseCompleted).disabled}
                                      onChange={() => handleToggleComplete(lesson.id)}
                                    />
                                  </div>
                                </Tooltip>
                                )}
                              </div>
                              <Tooltip title={lesson.title} placement="topLeft">
                                <span className="learn-lesson-title">
                                  {ellipsis(lesson.title, MAX_LESSON_NAME)}
                                </span>
                              </Tooltip>
                              <span style={{ marginLeft: 'auto' }}>
                                {lesson.type === "Video" ? (
                                  <i className="bi bi-camera-reels" style={{ color: "#1677ff", fontSize: 18, verticalAlign: "middle" }}></i>
                                ) : (
                                  <i className="bi bi-file-earmark-richtext" style={{ color: "#1677ff", fontSize: 18, verticalAlign: "middle" }}></i>
                                )}
                              </span>
                            </div>
                          ))}
                          
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}